/**
 * Registry de Painéis do Sistema
 *
 * Centraliza todos os painéis disponíveis com lazy loading
 * Separação: painéis genéricos vs painéis de domínio
 */

import logger from '@/services/logger'

export const PANEL_REGISTRY = {
  // === PAINÉIS GENÉRICOS/REUTILIZÁVEIS ===

  'add-content': {
    component: () => import('@/components/panels/AddContentForm.vue'),
    title: 'Adicionar Conteúdo',
    description: 'Formulário genérico para adicionar título e descrição',
  },

  'auth-password': {
    component: () => import('@/components/panels/AuthPasswordPanel.vue'),
    title: 'Senha de Autorização',
    description: 'Painel genérico de autenticação por senha de autorização',
    ephemeral: true, // não deve ser reconstruído após refresh (F5) — depende de callbacks em memória
  },

  'resume-item-details': {
    component: () => import('@/components/widgets/ResumeItemDetailsPanel.vue'),
    title: 'Detalhes',
    description: 'Painel genérico para exibir detalhes de itens de resumo',
  },

  'contact-info': {
    component: () => import('@/view/Layouts/DashContainer/SidePanels/SideContactInfo.vue'),
    title: 'Atendimento Imobanco',
    description: 'Painel com informações de contato e atendimento',
  },

  // === PAINÉIS DE DOMÍNIO ===

  'solicitation-detail': {
    component: () => import('@/view/pages/admin/antecipacao/SidePanels/SolicitationDetail.vue'),
    title: 'Detalhes da Solicitação',
    description: 'Informações completas da solicitação de antecipação',
  },

  'contract-card-details': {
    component: () =>
      import('@/view/pages/admin/antecipacao/cards/AntecipacoesContractCardDetailsPanel.vue'),
    title: 'Detalhes do Contrato',
    description: 'Painel genérico para exibir detalhes de cards de contrato',
  },

  'debts-by-payer': {
    component: () => import('@/view/pages/admin/antecipacao/SidePanels/DebtsByPayerPanel.vue'),
    title: 'Títulos por pagador',
    description: 'Painel com títulos agrupados por pagador',
  },

  'debts-payers-non-payment': {
    component: () =>
      import('@/view/pages/admin/antecipacao/SidePanels/DebtsPayersNonPaymentPanel.vue'),
    title: 'Inadimplência por pagador',
    description: 'Painel com taxa de inadimplência agrupada por pagador',
  },

  'debts-change-rates': {
    component: () => import('@/view/pages/admin/antecipacao/SidePanels/DebtsChangeRates.vue'),
    title: 'Edição de Taxas',
    description: 'Painel para editar taxas de antecipação de recebíveis',
  },

  'debts-confirm-operation': {
    component: () => import('@/view/pages/admin/antecipacao/SidePanels/DebtsConfirmOperation.vue'),
    title: 'Antecipação de recebíveis',
    description: 'Painel de confirmação de operação de antecipação',
  },

  'novo-bloqueio-valor': {
    component: () => import('@/view/pages/admin/bloqueios/SidePanel/NovoBloqueioValor.vue'),
    title: 'Novo Bloqueio de Valores',
    description: 'Painel para criar novo bloqueio de valores',
  },

  'reverter-liquidar-valores': {
    component: () => import('@/view/pages/admin/bloqueios/SidePanel/ReverterLiquidarValores.vue'),
    title: 'Reverter/Liquidar Bloqueio',
    description: 'Painel para reverter ou liquidar bloqueios de valores',
  },

  'bloquear-titulos': {
    component: () => import('@/view/pages/admin/bloqueios/SidePanel/BloquearTitulos.vue'),
    title: 'Bloquear/Desbloquear Títulos',
    description: 'Painel para bloquear ou desbloquear títulos',
  },

  'payment-create': {
    component: () => import('@/view/pages/public/checkout/Sidepanel/PaymentCreate.vue'),
    title: 'Pagamento',
    description: 'Criar plano de pagamento para venda',
  },

  'payment-boleto': {
    component: () => import('@/view/pages/public/checkout/Sidepanel/PaymentBoleto.vue'),
    title: 'Pagamento',
    description: 'Pagar parcela com boleto',
  },

  'payment-pix': {
    component: () => import('@/view/pages/public/checkout/Sidepanel/PaymentPix.vue'),
    title: 'Pagamento',
    description: 'Pagar parcela com PIX',
  },

  'payer-debt-detail': {
    component: () => import('@/view/pages/public/Payments/Sidepanels/PayerDebtDetail.vue'),
    title: 'Detalhes da cobrança',
    description: 'Detalhes da cobrança do pagador',
  },

  'deposit-modal': {
    component: () => import('@/view/pages/public/PublicDeposit/modal/DepositModal.vue'),
    title: 'Depósito PIX',
    description: 'Realizar depósito via PIX',
  },

  'contacts-map-columns': {
    component: () => import('@/view/pages/shared/contacts/SidePanels/ContactsMapColumns.vue'),
    title: 'Mapeamento',
    description: 'Mapear colunas de planilha de contatos',
  },

  'new-edit-contact-panel': {
    component: () => import('@/view/pages/shared/contacts/SidePanels/NewEditContactSidebar.vue'),
    title: 'Contato',
    description: 'Cadastrar ou editar contato',
  },

  'detail-contact-panel': {
    component: () => import('@/view/pages/shared/contacts/SidePanels/DetailContactSidebar.vue'),
    title: 'Contato',
    description: 'Detalhes do contato',
  },

  'address-form-panel': {
    component: () => import('@/view/pages/shared/contacts/SidePanels/AddressForm.vue'),
    title: 'Endereço',
    description: 'Cadastrar ou editar endereço do contato',
  },

  'bank-form-panel': {
    component: () => import('@/view/pages/shared/contacts/SidePanels/BankForm.vue'),
    title: 'Conta Bancária',
    description: 'Cadastrar ou editar conta bancária do contato',
  },

  'dependent-form-panel': {
    component: () => import('@/view/pages/shared/contacts/SidePanels/DependentForm.vue'),
    title: 'Novo dependente',
    description: 'Cadastrar novo dependente para o contato',
  },

  'lot-table-cell': {
    component: () => import('@/view/pages/shared/contacts/SidePanels/LotTableCell.vue'),
    title: 'Edição de linha',
    description: 'Editar linha da tabela de importação em lote',
  },

  'debts-lot-map-columns': {
    component: () => import('@/view/pages/shared/debts/SidePanels/DebtsLotMapColumns.vue'),
    title: 'Mapeamento de Colunas',
    description: 'Mapear colunas da planilha para campos de títulos',
  },

  'composite-split-create': {
    component: () => import('@/view/pages/shared/debts/SidePanels/SplitCreate.vue'),
    title: 'Criação e Edição de Splits',
    description: 'Adicionar ou editar beneficiários do carnê',
  },

  'debts-lot-table-cell': {
    component: () => import('@/view/pages/shared/debts/SidePanels/LotTableCell.vue'),
    title: 'Edição de linha',
    description: 'Editar linha da tabela de importação de títulos',
  },

  'create-debt': {
    component: () => import('@/view/pages/shared/debts/SidePanels/CreateDebt.vue'),
    title: 'Criação de Título',
    description: 'Criar novo título ou carnê de cobrança',
  },

  'edit-debt': {
    component: () => import('@/view/pages/shared/debts/SidePanels/EditDebt.vue'),
    title: 'Edição de Título',
    description: 'Editar informações do título',
  },

  'create-contract': {
    component: () => import('@/view/pages/shared/contracts/SidePanels/CreateContract.vue'),
    title: 'Criação de Contrato',
    description: 'Criar novo contrato de cobrança',
  },

  'debt-pix': {
    component: () => import('@/view/pages/shared/debts/SidePanels/DebtPixSidePanel.vue'),
    title: 'PIX',
    description: 'Visualizar ou gerar PIX para o título',
    ephemeral: true,
  },

  'deposit-recognition-create': {
    component: () => import('@/view/pages/shared/deposit/reconhecimento/Create.vue'),
    title: 'Novo Reconhecimento de Depósito',
    description: 'Cadastrar novo reconhecimento de depósito',
  },

  'product-split-drawer': {
    component: () => import('@/view/pages/shared/products/SidePanel/ProductSplitDrawer.vue'),
    title: 'Split de Pagamento',
    description: 'Adicionar ou editar split de pagamento',
  },

  'statement-ofx-export': {
    component: () => import('@/view/pages/shared/statement/SidePanel/ofx.vue'),
    title: 'Exportação extrato OFX',
    description: 'Exportar extrato em formato OFX',
  },

  'transfer-lot-map-columns': {
    component: () => import('@/view/pages/shared/transfers/SidePanels/TransferLotMapColumns.vue'),
    title: 'Mapeamento de Colunas',
    description: 'Mapeamento de colunas da planilha de transferências',
  },

  'transfer-lot-table-cell': {
    component: () => import('@/view/pages/shared/transfers/SidePanels/LotTableCell.vue'),
    title: 'Edição de linha',
    description: 'Edição de célula da tabela de transferências em lote',
  },

  'change-email': {
    component: () => import('@/view/pages/shared/user/SidePanel/changeEmail.vue'),
    title: 'Alteração de Email',
    description: 'Alterar email do usuário',
  },

  'change-password': {
    component: () => import('@/view/pages/shared/user/SidePanel/changePassword.vue'),
    title: 'Alteração de Senha',
    description: 'Alterar senha de acesso ou autorização',
  },

  'sale-review': {
    component: () => import('@/view/pages/shared/vendas/SidePanel/SaleReview.vue'),
    title: 'Pagamento',
    description: 'Revisão e confirmação de venda',
  },

  'wallet-panel': {
    component: () => import('@/view/pages/shared/wallets/SidePanel/WalletPanel.vue'),
    title: 'Carteiras',
    description: 'Criar ou editar carteira',
  },

  'notification-detail': {
    component: () => import('@/components/notification/NotificationDetail.vue'),
    title: 'Informações da Notificação',
    description: 'Detalhes da notificação',
  },

  'deny-interest': {
    component: () => import('@/view/pages/admin/antecipacao/SidePanels/DenyInterestPanel.vue'),
    title: 'Recusar Solicitação',
    description: 'Fluxo de recusa de solicitação de antecipação',
  },

  'cancel-advance-proposal': {
    component: () =>
      import('@/view/pages/admin/antecipacao/SidePanels/CancelamentoPropostaPanel.vue'),
    title: 'Cancelar Proposta de Antecipação',
    description: 'Fluxo de cancelamento de proposta de antecipação',
  },

  'proposal-create': {
    component: () => import('@/view/pages/admin/antecipacao/SidePanels/ProposalCreate.vue'),
    title: 'Cadastrar proposta',
    description: 'Selecionar solicitação para criar proposta de antecipação',
  },

  'pedido-antecipacao': {
    component: () => import('@/view/pages/shared/antecipacao/SidePanels/PedidoAntecipacao.vue'),
    title: 'Antecipação de Recebíveis',
    description: 'Formulário de solicitação de antecipação de recebíveis',
  },

  'client-solicitation-detail': {
    component: () => import('@/view/pages/shared/antecipacao/SidePanels/SolicitationDetail.vue'),
    title: 'Detalhes da Solicitação',
    description: 'Detalhes da solicitação de antecipação (visão cliente)',
  },

  'cancel-interest': {
    component: () => import('@/view/pages/shared/antecipacao/SidePanels/CancelInterestPanel.vue'),
    title: 'Cancelar Solicitação',
    description: 'Fluxo de cancelamento de solicitação de antecipação',
  },

  'approve-proposal': {
    component: () => import('@/view/pages/shared/antecipacao/SidePanels/ApproveProposal.vue'),
    title: 'Aprovar proposta',
    description: 'Fluxo de aprovação de proposta de antecipação',
  },

  'pay-overdue-advancement': {
    component: () => import('@/view/pages/shared/antecipacao/SidePanels/PayOverdueAdvancement.vue'),
    title: 'Abater valor em atraso',
    description: 'Fluxo de pagamento de saldo atrasado de débito antecipado com débito em conta',
  },

  'remove-debt': {
    component: () => import('@/view/pages/shared/antecipacao/SidePanels/RemoveDebt.vue'),
    title: 'Operação de Título',
    description: 'Fluxo de baixa manual e cancelamento de título da antecipação',
  },

  'amortize-advancement': {
    component: () => import('@/view/pages/shared/antecipacao/SidePanels/AmortizeAdvancement.vue'),
    title: 'Amortização Antecipada',
    description: 'Fluxo de amortização antecipada de títulos com débito em conta',
  },

  // === FUTURO: Fazer toda migração do front pra deixar rods, gaby e amanda felizes vide exemplo abaixo:

  'deny-proposal': {
    component: () => import('@/view/pages/shared/antecipacao/SidePanels/DenyProposal.vue'),
    title: 'Recusar proposta',
    description: 'Fluxo de recusa de proposta de antecipação',
  },

  'change-user-permissions': {
    component: () =>
      import('@/view/pages/shared/profile/SidePanels/ChangeAllowedUserPermissions.vue'),
    title: 'Alteração de Permissões',
    description: 'Gerenciar permissões de usuários permitidos',
  },

  'remove-allowed-user': {
    component: () => import('@/view/pages/shared/profile/SidePanels/RemoveAllowedUserPanel.vue'),
    title: 'Remoção de Usuário',
    description: 'Remover usuário da lista de permitidos',
  },

  'new-allowed-user': {
    component: () => import('@/view/pages/shared/profile/SidePanels/NewAllowedUserPanel.vue'),
    title: 'Cadastro de Usuário',
    description: 'Adicionar novo usuário permitido',
  },

  'indication-rules': {
    component: () => import('@/view/pages/shared/indication/SidePanels/IndicationRules.vue'),
    title: 'Regras de Indicação',
    description: 'Regras e informações sobre o programa de indicação',
  },

  'dados-pessoais': {
    component: () => import('@/view/pages/admin/clients/SidePanel/dadosPessoais.vue'),
    title: 'Dados pessoais',
    description: 'Alterar informações pessoais do cliente',
  },

  'dados-empresariais': {
    component: () => import('@/view/pages/admin/clients/SidePanel/dadosEmpresariais.vue'),
    title: 'Dados empresariais',
    description: 'Alterar informações empresariais do cliente',
  },

  'dados-socios': {
    component: () => import('@/view/pages/admin/clients/SidePanel/dadosSocios.vue'),
    title: 'Quadro Societário',
    description: 'Alterar dados do quadro societário',
  },

  'dados-endereco': {
    component: () => import('@/view/pages/admin/clients/SidePanel/dadosEndereco.vue'),
    title: 'Endereço',
    description: 'Alterar endereço cadastrado do cliente',
  },

  'dados-documentos': {
    component: () => import('@/view/pages/admin/clients/SidePanel/dadosDocumentos.vue'),
    title: 'Documentos',
    description: 'Alterar documentos do cliente',
  },

  'select-manager': {
    component: () => import('@/view/pages/admin/clients/SidePanel/selectManager.vue'),
    title: 'Executivo de Conta',
    description: 'Selecionar executivo de conta do cliente',
  },

  'event-panel': {
    component: () => import('@/view/pages/admin/clients/SidePanel/EventPanel.vue'),
    title: 'Detalhes do Evento',
    description: 'Visualizar detalhes de um evento do histórico do cliente',
  },

  'register-service-event': {
    component: () => import('@/view/pages/admin/clients/SidePanel/RegisterServiceInEventPanel.vue'),
    title: 'Cadastrar atendimento',
    description: 'Cadastrar número de atendimento do evento',
  },

  'analysis-panel': {
    component: () => import('@/view/pages/admin/clients/SidePanel/AnalysisPanel.vue'),
    title: 'Avaliação de Cadastro',
    description: 'Aprovar ou recusar cadastro do cliente',
  },

  'risk-classification': {
    component: () => import('@/view/pages/admin/clients/SidePanel/riskClassificationPanel.vue'),
    title: 'Classificação de risco',
    description: 'Alterar a classificação de risco do cadastro',
  },

  'general-observations': {
    component: () => import('@/view/pages/admin/clients/SidePanel/generalObservationsPanel.vue'),
    title: 'Observações gerais',
    description: 'Alterar as observações gerais do cadastro',
  },

  // Parcerias e Indicações
  'register-partner': {
    component: () => import('@/view/pages/admin/commission/SidePanels/RegisterPartnerPanel.vue'),
    title: 'Cadastro de parceria',
    description: 'Cadastrar um novo parceiro no sistema',
  },

  'create-plan': {
    component: () => import('@/view/pages/admin/commission/SidePanels/CreatePlanPanel.vue'),
    title: 'Criação de link de indicação',
    description: 'Criar novo plano de indicação com split de comissão',
  },

  'commission-detail': {
    component: () => import('@/view/pages/admin/commission/SidePanels/CommissionDetailPanel.vue'),
    title: 'Detalhes da comissão',
    description: 'Visualizar informações completas da comissão',
  },

  'plan-details': {
    component: () => import('@/view/pages/admin/commission/SidePanels/PlanDetailsPanel.vue'),
    title: 'Informações completas do plano',
    description: 'Visualizar detalhes do plano de indicação',
  },

  'set-indication-link-action': {
    component: () =>
      import('@/view/pages/admin/commission/SidePanels/SetIndicationLinkActionPanel.vue'),
    title: 'Alteração no link de indicação',
    description: 'Bloquear, reativar ou excluir link de indicação',
  },

  'set-partnership-action': {
    component: () =>
      import('@/view/pages/admin/commission/SidePanels/SetPartnershipActionPanel.vue'),
    title: 'Alteração na parceria',
    description: 'Pausar, reativar ou cancelar parceria',
  },

  'cancel-debenture': {
    component: () => import('@/view/pages/admin/debentures/SidePanel/CancelOperationPanel.vue'),
    title: 'Cancelamento de Debênture',
    description: 'Cancelar operação de debênture',
  },

  'debenture-detail': {
    component: () => import('@/view/pages/admin/debentures/SidePanel/details/DebentureDetail.vue'),
    title: 'Detalhes da Aplicação',
    description: 'Visualizar detalhes completos da aplicação de debênture',
  },

  'admin-reason': {
    component: () => import('@/view/pages/admin/clients/SidePanel/adminReasonPanel.vue'),
    title: 'Cadastro de atendimento',
    description: 'Registrar atendimento com senha de autorização',
  },

  'validation-acquisition-debenture': {
    component: () =>
      import('@/view/pages/admin/debentures/SidePanel/ValidationAcquisitionPanel.vue'),
    title: 'Validação de Debênture',
    description: 'Processo completo de validação de aquisição de debênture',
  },

  'create-debenture-proposal': {
    component: () =>
      import('@/view/pages/admin/debentures/SidePanel/CreateDebentureProposalPanel.vue'),
    title: 'Criação de Proposta',
    description: 'Criar nova proposta de aquisição de debênture',
  },

  'correct-transaction': {
    component: () => import('@/view/pages/admin/correct_transaction/CorrectTransactionPanel.vue'),
    title: 'Correção de Transação',
    description: 'Corrigir valores de transação e extrato',
    size: 'sm',
  },

  // ===== Admin Permissions =====
  'add-user-permission': {
    component: () => import('@/view/pages/admin/permissoes/SidePanels/AddUserPermissionPanel.vue'),
    title: 'Adicionar Usuário Administrativo',
    description: 'Criar novo usuário administrativo com permissões',
  },

  'visualize-admin-user': {
    component: () =>
      import('@/view/pages/admin/permissoes/SidePanels/VisualizeAdminUserPermission.vue'),
    title: 'Visualizar Usuário Administrativo',
    description: 'Visualizar dados e permissões do usuário administrativo',
  },

  'active-or-inactive-admin-user': {
    component: () => import('@/view/pages/admin/permissoes/SidePanels/ActiveOrInactivePanel.vue'),
    title: 'Ativar/Desativar Usuário',
    description: 'Ativar ou desativar usuário administrativo',
    size: 'sm',
  },

  'edit-data-admin-user': {
    component: () => import('@/view/pages/admin/permissoes/SidePanels/EditDataPanel.vue'),
    title: 'Editar Dados do Usuário',
    description: 'Editar informações do usuário administrativo',
    size: 'sm',
  },

  'edit-permissions-admin-user': {
    component: () => import('@/view/pages/admin/permissoes/SidePanels/EditPermissionsPanel.vue'),
    title: 'Editar Permissões',
    description: 'Editar permissões do usuário administrativo',
  },

  'authorize-transactions': {
    component: () =>
      import('@/view/pages/shared/authorization/SidePanels/AuthorizeTransactionsPanel.vue'),
    title: 'Autorizar Transações',
    description: 'Fluxo de revisão e autorização de transações em lote',
  },

  'transfer-panel': {
    component: () => import('@/view/pages/shared/transfers/SidePanels/TransferPanel.vue'),
    title: 'Realizar Transferência',
    description: 'Formulário de criação de transferência bancária ou digital',
  },

  'authorize-transfer': {
    component: () => import('@/view/pages/shared/transfers/SidePanels/AuthorizeTransferPanel.vue'),
    title: 'Revisar Transferência',
    description: 'Revisão e autorização de transferência individual',
  },

  'payment-panel': {
    component: () => import('@/view/pages/shared/pagamentos/SidePanel/PaymentPanel.vue'),
    title: 'Pagamento de Contas',
    description: 'Criar novo pagamento de boleto ou conta',
  },

  'pix-key': {
    component: () => import('@/view/pages/shared/pix/cash_out/DrawerPixKey.vue'),
    title: 'Transferência PIX',
    description: 'Enviar PIX usando chave PIX',
  },

  'pix-qrcode': {
    component: () => import('@/view/pages/shared/pix/cash_out/DrawerQrCode.vue'),
    title: 'PIX QRCode',
    description: 'Pagar PIX usando QRCode ou copia e cola',
  },

  'pix-charge': {
    component: () => import('@/view/pages/shared/pix/cash_in/DrawerPIXIn.vue'),
    title: 'Cobrança PIX',
    description: 'Gerar QRCode para receber pagamento',
  },

  'pix-receipt': {
    component: () => import('@/view/pages/shared/pix/Sidepanels/ReceiptPIX.vue'),
    title: 'Comprovante PIX',
    description: 'Visualizar comprovante de transação PIX',
  },

  'create-payment-order': {
    component: () =>
      import('@/view/pages/shared/payment_orders/SidePanels/CreatePaymentOrderPanel.vue'),
    title: 'Cadastrar Ordem de Pagamento',
    description: 'Criar nova ordem de pagamento com código de resgate',
  },

  'claim-payment-order': {
    component: () =>
      import('@/view/pages/shared/payment_orders/SidePanels/ClaimPaymentOrderPanel.vue'),
    title: 'Resgatar Ordem de Pagamento',
    description: 'Resgatar ordem de pagamento usando código',
  },

  'payment-order-details': {
    component: () =>
      import('@/view/pages/shared/payment_orders/SidePanels/PaymentOrderDetailsPanel.vue'),
    title: 'Detalhes da Ordem de Pagamento',
    description: 'Visualizar detalhes e código de resgate da ordem',
  },

  'deposit-create': {
    component: () => import('@/view/pages/shared/deposit/boleto/DepositCreate.vue'),
    title: 'Cobrar depósito por boleto',
    description: 'Criar novo boleto de depósito',
  },

  'imopix-create': {
    component: () => import('@/view/pages/shared/deposit/imopix/ImoPixCreate.vue'),
    title: 'Cobrar por PIX',
    description: 'Criar nova cobrança PIX de depósito',
  },

  'buy-debentures': {
    component: () =>
      import('@/view/pages/shared/investments/debentures/SidePanel/BuyDebenturesPanel.vue'),
    title: 'Aquisição de Debêntures',
    description: 'Comprar debêntures com fluxo completo de autorização',
  },

  'rescue-debentures': {
    component: () =>
      import('@/view/pages/shared/investments/debentures/SidePanel/RescueDebenturesPanel.vue'),
    title: 'Resgate de Debêntures',
    description: 'Resgatar debêntures com validação de senha',
  },

  'transfer-debentures': {
    component: () =>
      import('@/view/pages/shared/investments/debentures/SidePanel/TransferDebenturesPanel.vue'),
    title: 'Transferência de Debêntures',
    description: 'Transferir debêntures para outro cliente Imobanco',
  },

  'details-transfer-debentures': {
    component: () =>
      import('@/view/pages/shared/investments/debentures/SidePanel/DetailsTransferPanel.vue'),
    title: 'Detalhes de Transferência',
    description: 'Visualizar detalhes de transferência de debêntures',
  },

  'authorize-proposal': {
    component: () =>
      import('@/view/pages/shared/investments/debentures/SidePanel/AuthorizeProposalPanel.vue'),
    title: 'Autorizar proposta',
    description: 'Aprovar ou recusar proposta de aquisição de debênture',
    size: 'sm',
  },
}

/**
 * Helper para calcular tamanho baseado no nível
 * @param {number} level - Nível atual (1, 2, 3)
 * @param {number} totalLevels - Total de níveis no stack
 * @returns {string} - 'sm' | 'md' | 'lg'
 */
export function calculatePanelSize(level /*totalLevels*/) {
  // Nível 1 sempre ocupa o tamanho máximo para evitar "saltos" ao fechar níveis superiores
  if (level === 1) return 'lg'

  // Nível 2 sempre médio
  if (level === 2) return 'md'

  // Nível 3+ sempre pequeno
  return 'sm'
}

/**
 * Validar se painel existe no registry
 * @param {string} name - Nome do painel
 * @returns {boolean}
 */
export function validatePanelName(name) {
  if (!PANEL_REGISTRY[name]) {
    logger.error(`[SidePanel Registry] Panel "${name}" not found in registry`)
    return false
  }
  return true
}

/**
 * Obter informações do painel
 * @param {string} name - Nome do painel
 * @returns {object|null}
 */
export function getPanelInfo(name) {
  return PANEL_REGISTRY[name] || null
}
